import React, { FC, Fragment } from 'react';
import { Box, Paper } from '@mui/material';
import moment from 'moment';

import { useDateRange, useProtectedClient } from 'hooks';

import {
  OrganicInsightsAdvancedFilters,
  OrganicInsightsSortFields,
  OrganicInsightsSortParam,
  SortDirections,
  useGetClientPageMappingsQuery,
  useGetUsersByEmailAddressQuery,
} from 'api';
import { EmptyState } from 'components';

import { FilterBar, Header, Tables } from '../components';
import { useGetCompanies } from '../hooks';
import { OrganicInsightsCompanyParams } from '../helpers';
import { PAGINATION_LIMIT } from '../constants';

const OrganicInsightsCompanies: FC = () => {
  const client = useProtectedClient();
  const dateRangeOptions = useDateRange({ includeToday: true });
  const { startDate, endDate, calculateDateRange } = dateRangeOptions;

  const [searchParams, setSearchParams] = OrganicInsightsCompanyParams({
    start: startDate,
    end: endDate,
    hide_company: true,
    sort_by: JSON.stringify({
      field: OrganicInsightsSortFields.TotalFollowers,
      direction: SortDirections.Desc,
    }),
  });

  const userQueryOptions = useGetUsersByEmailAddressQuery(
    {
      email_address_list: searchParams?.users || [],
    },
    {
      enabled: searchParams?.users?.length > 0,
    }
  );

  const companyPagesQueryOptions = useGetClientPageMappingsQuery(
    {
      id: client.id,
    },
    {
      enabled: searchParams?.company_pages?.length > 0,
    }
  );

  const advanced_filters: OrganicInsightsAdvancedFilters = {
    users: userQueryOptions?.data?.getUsersByEmailAddress.map(value => value.id) || [],
    posts: searchParams?.posts,
    company_pages:
      companyPagesQueryOptions?.data?.getClientPageMappings
        .filter(element => searchParams?.company_pages?.includes(element.name))
        .map(value => value.linkedin_page_id) || [],
  };

  const queryOptions = useGetCompanies({
    start: moment(searchParams.start).unix(),
    end: moment(searchParams.end).unix(),
    offset: 0,
    limit: PAGINATION_LIMIT,
    sort_by: searchParams.sort_by,
    hide_company: searchParams.hide_company,
    advanced_filters: advanced_filters,
  });

  function updateTableColumnSort(param: OrganicInsightsSortParam) {
    setSearchParams({
      ...searchParams,
      sort_by: JSON.stringify(param[0]),
      users: searchParams?.users?.join(',') || '',
      posts: searchParams?.posts?.join(',') || '',
      company_pages: searchParams?.company_pages?.join(',') || '',
    });
  }

  function renderTable() {
    if (queryOptions.isEmpty) {
      return <EmptyState name="insightsCompanies" />;
    }

    return (
      <Tables.Companies
        onSort={updateTableColumnSort}
        sortBy={searchParams.sort_by}
        {...queryOptions}
      />
    );
  }

  return (
    <Fragment>
      <Box paddingBottom={2}>
        <Header filters={searchParams} advanced_filters={advanced_filters} />
      </Box>

      <Box paddingBottom={3}>
        <FilterBar
          filter={searchParams}
          setFilter={setSearchParams}
          {...dateRangeOptions}
          startDate={searchParams.start}
          endDate={searchParams.end}
          dateRange={calculateDateRange(searchParams.start, searchParams.end)}
          total={queryOptions.count}
          queryLoading={queryOptions.isInitialLoad}
        />
      </Box>

      <Paper>{renderTable()}</Paper>
    </Fragment>
  );
};

export default OrganicInsightsCompanies;
